import { useState } from "react";
import { Input, Button, Dropdown } from "@/config/antd";
import {
  SearchOutlined,
  ReloadOutlined,
  DownOutlined,
} from "@ant-design/icons";
import WebIcon from "@/components/common/Web_Icons";
import PropTypes from "prop-types";
import Calendar from "@/components/specific/Calender";
import MoreOptions from "@/components/specific/More_Options";
import { format } from "date-fns";

const SearchFilterBar = ({
  onSearch,
  onRefresh,
  onFilterChange,
  filterValue = "Today",
  className = "",
  placeholder = "Search...",
  moreOptionsMenu,
  "data-testid": dataTestId,
  markedDates = [],
  onMarkedDateClick,
  filterType = "calendar", // 'calendar' or 'dropdown'
  sortOptions = [], // options for sort dropdown values
}) => {
  const [isCalendarVisible, setIsCalendarVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setIsCalendarVisible(false);

    // Format date to YYYY-MM-DD for filtering - EXACTLY matching HomeCalender.jsx
    const formattedDate = date.toISOString().split("T")[0];

    // Update the filter text display
    const displayDate = format(date, "dd MMM yyyy");

    // Call the parent's filter change handler with both the display format and the filter format
    onFilterChange?.(displayDate, formattedDate);
  };

  // Handler for when a user clicks a date with events - direct filtering approach
  const handleMarkedDateClick = (date) => {
    setSelectedDate(date);
    setIsCalendarVisible(false);

    // Format date to YYYY-MM-DD for filtering - EXACTLY matching HomeCalender.jsx
    const formattedDate = date.toISOString().split("T")[0];

    // Update the filter text display with a more user-friendly format
    const displayDate = format(date, "dd MMM yyyy");

    // Call the parent's filter change handler with both the display format and the filter format
    onFilterChange?.(displayDate, formattedDate);

    // Use the specialized handler if available
    if (onMarkedDateClick) {
      onMarkedDateClick(date);
    }
  };

  const handleSortOptionClick = (key) => {
    onFilterChange?.(key);
  };

  // Create items for the sort dropdown
  const sortItems = sortOptions.map((option) => ({
    key: option.key,
    label: option.label,
    onClick: () => handleSortOptionClick(option.key),
  }));

  return (
    <div className="lg:hidden" data-testid={dataTestId || "search-filter-bar"}>
      <div className={`w-full flex flex-row items-center gap-2 ${className}`}>
        <Button
          icon={<ReloadOutlined />}
          onClick={onRefresh}
          className="flex items-center justify-center w-8 h-8 min-w-[32px] border border-primary text-primary hover:bg-gray-50 sm:w-10 sm:h-10"
          data-testid="refresh-button"
        />

        <Input
          placeholder={placeholder}
          prefix={<SearchOutlined className="text-primary" />}
          onChange={(e) => {
            const value = e.target.value;
            onSearch(value);
          }}
          className="flex-grow h-8 bg-secondary hover:bg-secondary focus:bg-secondary active:bg-secondary 
                     custom-search-input-placeholder sm:h-10 border-primary focus:border-primary focus:shadow-none"
          data-testid="event-search-input"
        />

        {filterType === "calendar" ? (
          <Button
            className="h-8 px-6 sm:h-10 sm:px-4 border-primary text-primary flex items-center gap-2"
            onClick={() => setIsCalendarVisible(true)}
            data-testid="filter-button">
            <WebIcon
              icon="BlueCalendar"
              size="small"
              className="w-4 h-4 flex-shrink-0"
            />
            <span>{filterValue}</span>
          </Button>
        ) : (
          <Dropdown
            menu={{ items: sortItems }}
            placement="bottomRight"
            trigger={["click"]}>
            <Button
              className="h-8 px-6 sm:h-10 sm:px-4 border-primary text-primary flex items-center gap-2"
              data-testid="sort-dropdown-button">
              <span className="text-primary text-small font-semibold">
                Sort by
              </span>
              <DownOutlined className="text-primary" />
            </Button>
          </Dropdown>
        )}

        <MoreOptions menu={moreOptionsMenu} data-testid="more-options" />
      </div>

      {filterType === "calendar" && (
        <Calendar
          isVisible={isCalendarVisible}
          onClose={() => setIsCalendarVisible(false)}
          onDateSelect={handleDateSelect}
          selectedDate={selectedDate}
          initialDate={selectedDate || new Date()}
          markedDates={markedDates}
          onMarkedDateClick={handleMarkedDateClick}
          data-testid="calendar-component"
        />
      )}
    </div>
  );
};

SearchFilterBar.propTypes = {
  onSearch: PropTypes.func,
  onRefresh: PropTypes.func,
  onFilterChange: PropTypes.func,
  filterValue: PropTypes.string,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  filterOptions: PropTypes.object,
  moreOptionsMenu: PropTypes.object,
  "data-testid": PropTypes.string,
  markedDates: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
  ),
  onMarkedDateClick: PropTypes.func,
  filterType: PropTypes.oneOf(["calendar", "dropdown"]),
  sortOptions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
    })
  ),
};

export default SearchFilterBar;
